import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconFileCheck } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { RecordPageHeader } from '../../components/RecordPageHeader';
import { SignatureBlock } from '@/components/SignatureBlock';
import { getClientDisplayName, getClientLegalName, mockClients } from '../caseload/data';
import {
  mockDischargeSummaries,
  mockEpisodes,
} from './data';
import type { DischargeSummary, Episode } from './data';

function formatDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (match) {
      const [, y, m, d] = match;
      return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

function DetailSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Text size="sm" fw={600} c="gray.7" mb={4}>
        {label}
      </Text>
      <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
        {children}
      </Text>
    </Box>
  );
}

export function DischargeSummaryPage(): JSX.Element {
  const { id: clientId, summaryId } = useParams<{ id: string; summaryId: string }>();
  const navigate = useNavigate();

  const client = useMemo(
    () => (clientId ? mockClients.find((c) => c.id === clientId) ?? null : null),
    [clientId],
  );

  const summary: DischargeSummary | null = useMemo(
    () => (summaryId ? mockDischargeSummaries.find((d) => d.id === summaryId) ?? null : null),
    [summaryId],
  );

  const episode: Episode | null = useMemo(
    () => (summary ? mockEpisodes.find((e) => e.id === summary.episodeId) ?? null : null),
    [summary],
  );

  if (!clientId) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          No client ID provided
        </Alert>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red" variant="light">
          Client not found
        </Alert>
      </Container>
    );
  }

  if (!summary) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Breadcrumbs>
            <Text component={Link} to="/dashboard/caseload" c="gold.7" size="sm" fw={500}>
              Caseload
            </Text>
            <Text component={Link} to={`/dashboard/clients/${clientId}`} c="gold.7" size="sm" fw={500}>
              {client.name}
            </Text>
            <Text size="sm" c="gray.6">
              Discharge Summary
            </Text>
          </Breadcrumbs>
          <Alert icon={<IconAlertCircle size={20} />} title="Not found" color="red" variant="light">
            Discharge summary not found
          </Alert>
        </Stack>
      </Container>
    );
  }

  const breadcrumbItems = [
    { title: 'Caseload', href: '/dashboard/caseload' },
    { title: client.name, href: `/dashboard/clients/${clientId}` },
    { title: 'Chart', href: `/dashboard/clients/${clientId}?tab=chart` },
    { title: 'Discharge Summary', href: '' },
  ].map((item, index) =>
    item.href ? (
      <Text key={index} component={Link} to={item.href} c="gold.7" size="sm" fw={500}>
        {item.title}
      </Text>
    ) : (
      <Text key={index} size="sm" c="gray.6">
        {item.title}
      </Text>
    ),
  );

  const chartHref = `/dashboard/clients/${clientId}`;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group gap="xs" align="center">
          <ActionIcon
            variant="subtle"
            color="gold.7"
            size="lg"
            onClick={() => navigate(`/dashboard/clients/${clientId}?tab=chart`)}
            aria-label="Go back"
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Group>

        <RecordPageHeader
          recordType="Discharge Summary"
          recordTypeColor="green"
          icon={<IconFileCheck size={12} style={{ color: 'var(--mantine-color-green-7)' }} />}
          secondaryTitle={episode ? (
            <Text component={Link} to={`/dashboard/clients/${clientId}?tab=chart&episodeId=${episode.id}`} c="gold.7" inherit style={{ textDecoration: 'none' }}>
              {episode.name}
            </Text>
          ) : undefined}
          primaryTitle={<Text component="span" c="gray.7">{formatDate(summary.dischargeDate)}</Text>}
          rightSide={
            <Badge color="green" variant="light" size="lg" styles={{ root: { textTransform: 'none', fontWeight: 500 } }}>
              Signed
            </Badge>
          }
          clientHref={chartHref}
          clientPreferredName={getClientDisplayName(client)}
          clientLegalName={(() => {
            const legal = getClientLegalName(client);
            const preferred = getClientDisplayName(client);
            return legal && legal !== preferred ? legal : undefined;
          })()}
          clientPronouns={client.pronouns}
          clientDob={client.dateOfBirth ? formatDate(client.dateOfBirth) : undefined}
          clientPhone={client.phone}
          clientEmail={client.email}
        />

        {/* Main content — separate block like Appointment page */}
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            {/* Meta row (discharge date in header; no duplicate) */}
            <Group gap="xl" wrap="wrap">
              {episode && (
                <Box>
                  <Text size="xs" tt="uppercase" fw={600} c="gray.5" mb={2}>
                    Episode Period
                  </Text>
                  <Text size="sm" fw={500}>
                    {formatDate(episode.startDate)}
                    {episode.endDate ? ` \u2013 ${formatDate(episode.endDate)}` : ' \u2013 Present'}
                  </Text>
                </Box>
              )}
              {episode && (
                <Box>
                  <Text size="xs" tt="uppercase" fw={600} c="gray.5" mb={2}>
                    Provider
                  </Text>
                  <Text size="sm" fw={500}>
                    {episode.provider}
                  </Text>
                </Box>
              )}
            </Group>

            <Divider />

            {/* Content sections */}
            <Stack gap="lg">
              <DetailSection label="Reason for Discharge">
                {summary.reasonForDischarge || '--'}
              </DetailSection>

              <DetailSection label="Summary of Care">
                {summary.summaryOfCare || '--'}
              </DetailSection>

              <DetailSection label="Recommendations">
                {summary.recommendations || '--'}
              </DetailSection>
            </Stack>

            {(summary.signedBy || summary.signedAt) && (
              <>
                <Divider />
                <SignatureBlock
                  label="Provider"
                  signature={{ name: summary.signedBy, signedAt: summary.signedAt }}
                />
              </>
            )}

            {/* Related treatment plan link */}
            {episode && (
              <>
                <Divider />
                <Group>
                  <Button
                    component={Link}
                    to={`/dashboard/clients/${clientId}/episode-of-care/${episode.id}`}
                    variant="light"
                    size="sm"
                  >
                    View Episode of Care
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
